/**
 * "¿Cómo la querés?" — el diálogo de exportación de la lista de productos.
 *
 * Una sola pregunta con tres tarjetas, el mismo patrón del selector del alta
 * guiada: el dueño está por mandarle la lista a un proveedor y elige el
 * formato según el proveedor, no según el sistema.
 *
 * El caso con historia es el TEXTO. En el celular, `navigator.share` abre la
 * hoja de compartir y de ahí a WhatsApp es un toque. En escritorio no existe:
 * se copia al portapapeles. Y cuando el portapapeles también falla (permisos,
 * navegadores embebidos), el texto se muestra acá mismo para copiarlo a mano:
 * la lista ya está armada, sería absurdo perderla por un permiso.
 */
import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { mostrarError, mostrarExito } from "@/lib/errores";
import type { Producto } from "@/lib/tipos";

import { descargarExcel, descargarPdf, filasParaExportar, textoLista } from "./exportar";

export interface DialogExportarProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** El nombre de la sección activa ("Perfumes"): titula el archivo y la lista. */
  seccion: string;
  /** El nombre del negocio, para el encabezado del PDF y del texto. */
  negocio: string;
  /** Lo que se está viendo en pantalla: la sección activa con sus filtros. */
  productos: ReadonlyArray<Producto>;
}

type Formato = "excel" | "pdf" | "texto";

const OPCIONES: ReadonlyArray<{
  formato: Formato;
  icono: typeof FileSpreadsheet;
  titulo: string;
  detalle: string;
}> = [
  {
    formato: "excel",
    icono: FileSpreadsheet,
    titulo: "Excel",
    detalle: "Una planilla: el proveedor agrega su columna de precio y la devuelve.",
  },
  {
    formato: "pdf",
    icono: FileText,
    titulo: "PDF",
    detalle: "La tabla prolija, para el que solo la va a leer.",
  },
  {
    formato: "texto",
    icono: MessageCircle,
    titulo: "Texto para WhatsApp",
    detalle: "La lista lista para pegar en el chat.",
  },
];

export function DialogExportar({
  abierto,
  onAbiertoChange,
  seccion,
  negocio,
  productos,
}: DialogExportarProps): JSX.Element {
  const [exportando, setExportando] = useState<Formato | null>(null);
  /** Se llena solo cuando compartir Y copiar fallaron: el plan C es copiar a mano. */
  const [textoAMano, setTextoAMano] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setExportando(null);
    setTextoAMano(null);
  }, [abierto]);

  async function compartirTexto(): Promise<void> {
    const texto = textoLista({ titulo: seccion, negocio, filas: filasParaExportar(productos) });

    // La hoja de compartir del celular: de ahí a WhatsApp es un toque. Cancelar
    // el share no es un error, así que se corta acá sin toast.
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ text: texto });
        onAbiertoChange(false);
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        // Sigue al portapapeles: hay navegadores que ofrecen share y lo rechazan.
      }
    }

    try {
      await navigator.clipboard.writeText(texto);
      onAbiertoChange(false);
      mostrarExito("Lista copiada", "Pegala en el chat del proveedor.");
    } catch {
      setTextoAMano(texto);
    }
  }

  async function exportar(formato: Formato): Promise<void> {
    setExportando(formato);
    try {
      if (formato === "excel") {
        await descargarExcel({ seccion, filas: filasParaExportar(productos) });
        onAbiertoChange(false);
        mostrarExito("Planilla descargada");
      } else if (formato === "pdf") {
        await descargarPdf({ seccion, negocio, filas: filasParaExportar(productos) });
        onAbiertoChange(false);
        mostrarExito("PDF descargado");
      } else {
        await compartirTexto();
      }
    } catch (error) {
      mostrarError(error);
    } finally {
      setExportando(null);
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exportar {seccion.toLowerCase()}</DialogTitle>
          <DialogDescription>
            {productos.length} producto{productos.length === 1 ? "" : "s"}, con su costo actual — lo
            que ves en pantalla, con los filtros puestos. Sin precios de venta ni márgenes.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {textoAMano === null ? (
            <div className="flex flex-col gap-2" role="group" aria-label="Formato">
              {/* Tres tarjetas-boton: elegir es exportar. La elegida queda
                  marcada con `aria-pressed` y con el spinner mientras arma el
                  archivo; las otras se apagan para que no se pidan dos. */}
              {OPCIONES.map((o) => (
                <Button
                  key={o.formato}
                  type="button"
                  variant="outline"
                  aria-pressed={exportando === o.formato}
                  cargando={exportando === o.formato}
                  disabled={exportando !== null}
                  onClick={() => void exportar(o.formato)}
                  className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
                >
                  {exportando === o.formato ? null : (
                    <o.icono className="text-muted-foreground" aria-hidden="true" />
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{o.titulo}</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      {o.detalle}
                    </span>
                  </span>
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                No se pudo copiar solo (el navegador no dio permiso). Seleccioná el texto y copialo
                a mano:
              </p>
              <Textarea
                readOnly
                aria-label="Texto de la lista"
                value={textoAMano}
                onFocus={(e) => e.currentTarget.select()}
                className="h-48"
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
            {textoAMano === null ? "Cancelar" : "Listo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
